import { useDroppable } from "@dnd-kit/core";
import { Flag } from "lucide-react";
import * as React from "react";

import { Badge, Button, Card, Skeleton } from "@/components/ui";
import { cn } from "@/lib/utils";
import { User, VideoProject } from "@/services/bedrock";

import { KanbanCard } from "./KanbanCard";
import { KanbanColumnDef, KanbanColumnId } from "./kanbanColumns";

export const KANBAN_DROP_PLACEHOLDER_ATTR = "data-kanban-drop-placeholder";

export type KanbanColumnDropState = "idle" | "valid" | "valid-hover" | "invalid" | "source";

const EMPTY_PROJECTS: VideoProject[] = [];
const COLUMN_SKELETON_COUNT = 3;

const SKELETON_LAYOUTS = [
  { title: "w-[72%]", meta: "w-16", date: "w-20" },
  { title: "w-[58%]", meta: "w-24", date: "w-16" },
  { title: "w-[80%]", meta: "w-14", date: "w-24" },
] as const;

interface KanbanColumnProps {
  column: KanbanColumnDef;
  projects: VideoProject[];
  totalCount: number;
  totalPriorityCount?: number;
  priorityOnly?: boolean;
  loading?: boolean;
  disabled?: boolean;
  pendingProjectId?: number | null;
  isDragging?: boolean;
  activeDragId?: string | null;
  activeProjectId?: string | null;
  collapseActiveCard?: boolean;
  dropPlaceholderIndex?: number | null;
  dropPlaceholderHeight?: number;
  dropState?: KanbanColumnDropState;
  hasMore?: boolean;
  loadingMore?: boolean;
  isCardDraggable: (project: VideoProject) => boolean;
  focusedProjectId?: number | null;
  staffUsers: User[];
  currentUserId?: number;
  onAssigneeChange?: (project: VideoProject, userId: number | null) => void;
  onQaChange?: (project: VideoProject, userId: number | null) => void;
  onStatusChange?: (project: VideoProject, columnId: KanbanColumnId) => void;
  onLoadMore?: (columnId: KanbanColumnId) => void;
}

function KanbanDropPlaceholder({ height }: { height?: number }) {
  return (
    <div
      aria-hidden="true"
      data-kanban-drop-placeholder=""
      className="rounded-lg bg-emerald-50/70 ring-2 ring-dashed ring-emerald-400/80"
      style={height != null ? { height } : { minHeight: 72 }}
    />
  );
}

function KanbanCardSkeleton({ variant = 0, className }: { variant?: number; className?: string }) {
  const layout = SKELETON_LAYOUTS[variant % SKELETON_LAYOUTS.length];

  return (
    <Card
      size="sm"
      aria-hidden="true"
      className={cn("pointer-events-none gap-0 overflow-hidden data-[size=sm]:py-0 ring-foreground/10", className)}
    >
      <div
        className="flex flex-col gap-2 p-2.5 [&_[data-slot=skeleton]]:[animation-delay:var(--skeleton-delay)]"
        style={{ "--skeleton-delay": `${variant * 140}ms` } as React.CSSProperties}
      >
        <div className="flex items-center justify-between gap-2">
          <Skeleton className="h-3 w-12" />
          <div className="flex items-center gap-1.5">
            <Skeleton className="size-6 rounded-full" />
            <Skeleton className="size-6 rounded-full" />
          </div>
        </div>
        <div className="flex items-start gap-2.5">
          <Skeleton className="size-16 shrink-0 rounded-md" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5 pt-0.5">
            <Skeleton className={cn("h-4", layout.title)} />
            <Skeleton className={cn("h-3", layout.meta)} />
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
        </div>
        <Skeleton className={cn("h-3", layout.date)} />
      </div>
    </Card>
  );
}

function KanbanColumnSkeletons() {
  return (
    <div className="flex flex-col gap-2 animate-in fade-in duration-200" aria-hidden="true">
      {Array.from({ length: COLUMN_SKELETON_COUNT }, (_, index) => (
        <KanbanCardSkeleton
          key={index}
          variant={index}
          className={index === 1 ? "opacity-75" : index === 2 ? "opacity-40" : undefined}
        />
      ))}
    </div>
  );
}

export const KanbanColumn = React.memo(function KanbanColumn({
  column,
  projects,
  totalCount,
  totalPriorityCount = 0,
  priorityOnly = false,
  loading = false,
  disabled = false,
  pendingProjectId = null,
  isDragging = false,
  activeDragId = null,
  activeProjectId = null,
  collapseActiveCard = false,
  dropPlaceholderIndex = null,
  dropPlaceholderHeight,
  dropState = "idle",
  hasMore = false,
  loadingMore = false,
  isCardDraggable,
  focusedProjectId = null,
  staffUsers,
  currentUserId,
  onAssigneeChange,
  onQaChange,
  onStatusChange,
  onLoadMore,
}: KanbanColumnProps) {
  const { setNodeRef } = useDroppable({
    id: column.id,
    disabled: isDragging && dropState === "invalid",
  });

  const cards = (projects.length ? projects : EMPTY_PROJECTS).map(project => (
    <KanbanCard
      key={project.id}
      videoProject={project}
      staffUsers={staffUsers}
      currentUserId={currentUserId}
      disabled={disabled || pendingProjectId === project.id}
      draggable={isCardDraggable(project) && pendingProjectId !== project.id}
      collapseInPlace={collapseActiveCard && activeProjectId === String(project.id)}
      isDragSource={activeDragId === String(project.id)}
      selected={focusedProjectId === project.id}
      onAssigneeChange={onAssigneeChange}
      onQaChange={onQaChange}
      onStatusChange={onStatusChange}
    />
  ));

  if (dropPlaceholderIndex != null) {
    cards.splice(
      Math.min(dropPlaceholderIndex, cards.length),
      0,
      <KanbanDropPlaceholder key="kanban-drop-placeholder" height={dropPlaceholderHeight} />
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col min-w-60 flex-1 transition-opacity duration-200",
        dropState === "invalid" && "opacity-45"
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between mb-3 px-0.5 transition-colors",
          dropState === "invalid" && "text-slate-400"
        )}
      >
        <h3 className="text-xs font-semibold leading-tight">{column.label}</h3>
        <div className="flex items-center gap-1.5">
          {loading ? (
            <>
              {column.id !== "completed" ? <Skeleton className="h-[22px] w-9 rounded-full" /> : null}
              {!priorityOnly || column.id === "completed" ? <Skeleton className="h-[22px] w-7 rounded-full" /> : null}
            </>
          ) : (
            <>
              {column.id !== "completed" ? (
                <Badge
                  size="small"
                  className={cn(
                    "gap-1 font-bold",
                    dropState === "invalid"
                      ? "border-transparent bg-slate-200 text-slate-400"
                      : totalPriorityCount > 0
                      ? "border-transparent bg-sky-500 text-white shadow-sm hover:bg-sky-500"
                      : "border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-50"
                  )}
                  title={`${totalPriorityCount} priority`}
                  aria-label={`${totalPriorityCount} priority ${totalPriorityCount === 1 ? "project" : "projects"}`}
                >
                  <Flag className="size-3 fill-current" aria-hidden="true" />
                  {totalPriorityCount}
                </Badge>
              ) : null}
              {!priorityOnly || column.id === "completed" ? (
                <Badge
                  variant="outline"
                  size="small"
                  className={cn(
                    "bg-white border-slate-200",
                    dropState === "invalid" ? "text-slate-400" : "text-slate-600"
                  )}
                  title={`${totalCount} total`}
                  aria-label={`${totalCount} total ${totalCount === 1 ? "project" : "projects"}`}
                >
                  {totalCount}
                </Badge>
              ) : null}
            </>
          )}
        </div>
      </div>
      <div
        ref={setNodeRef}
        aria-busy={loading || loadingMore}
        className={cn(
          "flex flex-col gap-2 min-h-[200px] flex-1 rounded-lg border border-dashed p-2 transition-[border-color,background-color,box-shadow] duration-200",
          dropState === "idle" && "border-slate-200 bg-slate-50/40",
          dropState === "source" && "border-slate-300 bg-slate-100/50",
          dropState === "invalid" && "border-slate-200 bg-slate-100/80 cursor-not-allowed",
          dropState === "valid" && "border-emerald-400 bg-emerald-50/40 ring-1 ring-emerald-200/60",
          dropState === "valid-hover" && "border-emerald-500 bg-emerald-50/70 ring-2 ring-emerald-300/70"
        )}
      >
        {loading || loadingMore ? (
          <span className="sr-only" role="status">
            {loading ? `Loading ${column.label} projects` : `Loading more ${column.label} projects`}
          </span>
        ) : null}
        {loading ? <KanbanColumnSkeletons /> : cards}
        {!loading && projects.length === 0 && !isDragging && (
          <div className="text-[11px] text-slate-400 text-center py-4 px-2">No projects</div>
        )}
        {!loading &&
          hasMore &&
          !isDragging &&
          (loadingMore ? (
            <KanbanCardSkeleton className="animate-in fade-in duration-200 opacity-70" />
          ) : (
            <Button
              variant="ghost"
              size="xs"
              className="w-full text-[11px] text-slate-600 hover:text-slate-900"
              disabled={disabled}
              onClick={() => onLoadMore?.(column.id)}
            >
              Load More
            </Button>
          ))}
      </div>
    </div>
  );
});
